import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { welcomeRequired } from "@/lib/welcomeConsent";
import { isAutomatedBrowser } from "@/lib/bots";
import { Languages, X } from "lucide-react";

const SUPPORTED = ["fr", "en", "es", "pt"];
const LABELS = { fr: "Français", en: "English", es: "Español", pt: "Português" };
// Shown in the DETECTED language so the visitor recognises the offer (not admin-editable:
// the client only holds the translations of the CURRENT UI language, never the detected one).
const COPY = {
  fr: { q: "Voir Yalola en français ?", yes: "Oui, en français" },
  en: { q: "View Yalola in English?", yes: "Yes, in English" },
  es: { q: "¿Ver Yalola en español?", yes: "Sí, en español" },
  pt: { q: "Ver o Yalola em português?", yes: "Sim, em português" },
};

// First browser-preferred language we actually support (ordered navigator.languages).
// Robot / navigateur automatisé : aucune langue annoncée → pas de bandeau « Voir en English ? ».
const deviceLang = () => {
  if (isAutomatedBrowser()) return null;
  const cands = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
  for (const l of cands) {
    const code = (l || "").slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(code)) return code;
  }
  return null;
};

const DISMISS_KEY = "yalola.langBanner.dismissed";

// Gentle "View in <your language>?" nudge when the device language differs from the
// language currently displayed. One click switches; dismissal is remembered per target
// language so it never nags twice for the same language.
export const LanguageBanner = () => {
  const { lang, setLang } = useApp();
  const detected = deviceLang();
  const [hidden, setHidden] = useState(false);

  if (hidden || !detected || detected === lang) return null;
  if (localStorage.getItem(DISMISS_KEY) === detected) return null;
  if (welcomeRequired()) return null;   // l'écran « Bienvenue » (app native) propose déjà la langue

  const copy = COPY[detected] || COPY.fr;
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, detected); } catch {}
    setHidden(true);
  };
  const switchLang = () => {
    setLang(detected);
    setHidden(true);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[45] w-[calc(100%-1.5rem)] max-w-md lang-banner-in"
      style={{ top: "calc(var(--sa-top, 0px) + 4rem)" }}
      data-testid="language-banner"
    >
      <div
        className="flex items-center gap-3 rounded-2xl px-3.5 py-2.5 shadow-lg border"
        style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}
      >
        <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
          <Languages size={18} />
        </div>
        <div className="flex-1 min-w-0 text-sm font-medium leading-tight" style={{ color: "var(--yalola-text)" }}>
          {copy.q}
        </div>
        <button
          type="button"
          onClick={switchLang}
          className="pill pill-primary shrink-0 h-9 px-3.5 text-sm font-semibold"
          data-testid="language-banner-switch"
        >
          {LABELS[detected] || copy.yes}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 h-8 w-8 grid place-items-center rounded-full transition-colors hover:bg-black/5"
          style={{ color: "var(--yalola-text-muted)" }}
          aria-label="Fermer"
          data-testid="language-banner-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default LanguageBanner;
